import { Space } from '@mantine/core';
import { IconCalendarDot, IconListCheck, IconPhoneSpark } from '@tabler/icons-react';
import classNames from 'classnames/bind';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { MenuItemBadges } from 'modules/Navigation/components';
import { useState } from 'react';
import type { RecoverActionType, RecoveryPlanStep } from 'types/recovery-plan';
import { openRecoveryTaskStepModal } from './openRecoveryTaskStepModal';
import styleIdentifiers from './RecoveryStepForm.module.scss';
import { RecoveryStepFormBody, type RecoveryStepFormType } from './RecoveryStepFormBody';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  step?: RecoveryPlanStep;
  availableTemplateTypes: RecoverActionType[];
  onSelectTemplate: (step: RecoveryPlanStep) => void;
  onClose: () => void;
  onlyTask?: boolean;
  onlyReminder?: boolean;
};

export default function RecoveryStepForm({
  step,
  availableTemplateTypes,
  onSelectTemplate,
  onClose,
  onlyTask = false,
  onlyReminder = false,
}: Props) {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();

  const getType = (): RecoveryStepFormType | undefined => {
    if (onlyTask) {
      return 'task_generation';
    }
    if (onlyReminder) {
      return 'send_reminder';
    }
    if (!step?.step_type) {
      return undefined;
    }
    switch (step.step_type) {
      case 'task_generation':
        return step.step_type;
      case 'initiate_ai_call':
        return step.step_type;
      default:
        return 'send_reminder';
    }
  };

  const getTitle = () => {
    if (onlyTask) {
      return t(i18nKeys.ADD_TASK);
    }
    if (onlyReminder) {
      return t(i18nKeys.ADD_REMINDER);
    }
    return t(i18nKeys.PLAN.EDITION_STEP);
  };

  const [type, setType] = useState(getType());

  const getActionStyles = (isEnabled: boolean): React.CSSProperties => ({
    borderColor: isEnabled ? '' : 'rgba(180, 190, 200, 0.6)',
    backgroundColor: isEnabled ? '' : 'rgba(248, 249, 250, 0.5)',
    pointerEvents: isEnabled ? 'auto' : 'none',
  });

  const getContentOpacity = (isEnabled: boolean) => ({
    opacity: isEnabled ? 1 : 0.4,
  });

  if (!type) {
    return (
      <div className={styles('recovery-step-form')}>
        <div className={styles('head')}>{getTitle()}</div>
        <div className={styles('content')}>
          <div className={styles('action')} onClick={() => setType('send_reminder')}>
            <IconCalendarDot stroke="1.5" size={30} />
            <Space h="sm" />
            {t(i18nKeys.FORM.RECOVER_STEP.TYPE.SEND_REMINDER)}
          </div>
          <div
            onClick={() => {
              openRecoveryTaskStepModal({
                title: t(i18nKeys.ADD_TASK),
                onSubmit: onSelectTemplate,
              });
              onClose();
            }}
            className={styles('action')}
            style={getActionStyles(company.package.can_use_tasks)}
          >
            <div style={getContentOpacity(company.package.can_use_tasks)}>
              <IconListCheck stroke="1.5" size={30} />
              <Space h="sm" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={getContentOpacity(company.package.can_use_tasks)}>
                {t(i18nKeys.FORM.RECOVER_STEP.TYPE.CREATE_TASK)}
              </div>
              {!company.package.can_use_tasks && <MenuItemBadges showUpsell />}
            </div>
          </div>
          <div
            onClick={() => setType('initiate_ai_call')}
            className={styles('action')}
            style={getActionStyles(company.package.can_use_ai_calls)}
          >
            <div style={getContentOpacity(company.package.can_use_ai_calls)}>
              <IconPhoneSpark stroke="1.5" size={30} />
              <Space h="xs" />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={getContentOpacity(company.package.can_use_ai_calls)}>
                {t(i18nKeys.FORM.RECOVER_STEP.TYPE.AI_CALL)}
              </div>
              {!company.package.can_use_ai_calls && <MenuItemBadges showUpsell />}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RecoveryStepFormBody
      type={type}
      title={getTitle()}
      step={step}
      availableTemplateTypes={availableTemplateTypes}
      onSelectTemplate={onSelectTemplate}
    />
  );
}
